import { useEffect, useRef, useState } from "react";
import api, { setToken } from "@/lib/api";
import { toast } from "sonner";

const SAVED_SLUG_KEY = "agri_saved_slug";

export default function PinLogin({ onSuccess }) {
  const [slug, setSlug] = useState(() => {
    try { return localStorage.getItem(SAVED_SLUG_KEY) || ""; } catch { return ""; }
  });
  const [rememberSlug, setRememberSlug] = useState(() => {
    try { return !!localStorage.getItem(SAVED_SLUG_KEY); } catch { return true; }
  });
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const slugRef = useRef(null);
  const pinRef = useRef(null);

  useEffect(() => {
    // Jump straight to PIN if the Business ID is already remembered.
    // Deliberately mount-only: re-running this on every `slug` keystroke
    // would steal focus back to the PIN field while the user is typing.
    (slug ? pinRef : slugRef).current?.focus();
  }, []);

  const submit = async () => {
    if (!slug.trim()) {
      toast.error("Enter your Business ID");
      slugRef.current?.focus();
      return;
    }
    if (pin.length < 4 || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { slug: slug.trim(), pin });
      setToken(data.token);
      try {
        if (rememberSlug) localStorage.setItem(SAVED_SLUG_KEY, slug.trim());
        else localStorage.removeItem(SAVED_SLUG_KEY);
      } catch {
        // ignore unavailable storage (e.g. private browsing)
      }
      onSuccess();
    } catch (e) {
      setPin("");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      toast.error(e?.response?.data?.detail || "Incorrect Business ID or PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900 px-4">
      <div className={`w-full max-w-xs ${shake ? "animate-[shake_0.4s]" : ""}`} data-testid="pin-login">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo/logo.png" alt="AgriLedger" className="h-14 w-14 rounded-xl mb-3" />
          <h1 className="text-white text-xl font-semibold tracking-tight">AgriLedger</h1>
          <p className="text-slate-400 text-sm mt-1">Enter your Business ID and PIN to continue</p>
        </div>

        <div className="space-y-1.5 mb-3">
          <input
            ref={slugRef}
            data-testid="login-slug"
            type="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") pinRef.current?.focus(); }}
            placeholder="Business ID"
            disabled={loading}
            className="w-full h-11 px-4 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <label className="flex items-center gap-2 pl-1 text-xs text-slate-400 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="remember-slug"
              checked={rememberSlug}
              onChange={(e) => setRememberSlug(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 accent-amber-400"
            />
            Remember my Business ID on this device
          </label>
        </div>

        <input
          ref={pinRef}
          data-testid="login-pin"
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="PIN"
          disabled={loading}
          className="w-full h-11 px-4 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm font-data tracking-[0.3em] placeholder:text-slate-500 placeholder:tracking-normal placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-amber-400"
        />

        <button data-testid="pin-submit" disabled={pin.length < 4 || loading} onClick={submit}
          className="w-full mt-4 h-12 rounded-xl bg-amber-400 text-slate-900 font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-500 transition-colors">
          {loading ? "Checking…" : "Unlock"}
        </button>
      </div>
    </div>
  );
}
